import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Badge,
  Box,
  Card,
  Divider,
  Group,
  Menu,
  Stack,
  Text,
  ThemeIcon,
  UnstyledButton,
} from '@mantine/core';
import { IconChevronRight, IconDots, IconMapPin, IconTrash, IconWheat } from '@tabler/icons-react';
import CropYearBadge from '@/components/app/buy/CropYearBadge';
import PriceRangeHint from '@/components/app/buy/PriceRangeHint';
import UpdatedDateBadge from '@/components/app/buy/UpdatedDateBadge';
import type { BasisGroup } from '@/lib/buyTree';
import type { PriceListGroup } from '@/types/buy';
import classes from './BasisCard.module.css';
import { formatOrgName } from '@/lib/format';

interface BasisCardProps {
  readonly basis: BasisGroup;
  readonly onOpen: (group: PriceListGroup) => void;
  /** Удалить все прайс-листы базиса (с подтверждением на стороне вызова). */
  readonly onDelete?: () => void;
}

// Карточка базиса: шапка (точка выгрузки) + строки-товары (мои прайс-листы на этом базисе).
// Базис — группировка моих объявлений по адресу, поэтому он контейнер, а не отдельный экран.
// В строке товара показываем диапазон моих цен (PriceRangeHint) — список несёт данные, не оглавление.
export default function BasisCard({ basis, onOpen, onDelete }: BasisCardProps) {
  const { t } = useTranslation();

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" wrap="nowrap" align="flex-start">
        <Group gap="sm" wrap="nowrap" align="flex-start" style={{ minWidth: 0 }}>
          <ThemeIcon variant="light" color="brand" size={40} radius="md">
            <IconMapPin size={22} />
          </ThemeIcon>
          <Stack gap={2} style={{ minWidth: 0 }}>
            <Text fw={700} truncate title={formatOrgName(basis.addressTitle)}>
              {formatOrgName(basis.addressTitle)}
            </Text>
            <Text size="sm" c="gray.7" truncate title={basis.address}>
              {basis.address}
            </Text>
          </Stack>
        </Group>
        <Group gap={6} wrap="nowrap" style={{ flexShrink: 0 }}>
          {/* Счётчик объектов — НЕЙТРАЛЬНЫЙ серый (не зелёный): это количество, а не статус (таксономия бейджей). */}
          <Badge variant="light" color="gray" tt="none">
            {t('app.buy.priceLists', { count: basis.items.length })}
          </Badge>
          {onDelete ? (
            // Деструктивное действие — за меню, а не на расстоянии misclick от навигации.
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" color="gray" aria-label={t('app.buy.del.basisTitle')}>
                  <IconDots size={18} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item color="red" leftSection={<IconTrash size={16} />} onClick={onDelete}>
                  {t('app.buy.del.basisButton')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          ) : null}
        </Group>
      </Group>

      <Divider my="sm" />

      <Stack gap={4}>
        {basis.items.map((group) => (
          <UnstyledButton
            key={`${group.goods}|${group.year}`}
            className={classes.goodsRow}
            onClick={() => onOpen(group)}
            aria-label={`${group.goods}, ${t('app.buy.cropYear', { year: group.year })} — ${formatOrgName(basis.addressTitle)}`}
          >
            {/* wrap: если культуре с бейджами и цене тесно в одной строке (телефон), цена уходит на вторую
                строку и прижимается вправо, а не сжимает название культуры до нуля. */}
            <Group justify="space-between" wrap="wrap" gap="xs">
              <Group gap="sm" wrap="nowrap" style={{ minWidth: 0, flex: '1 1 12rem' }}>
                <IconWheat size={20} color="var(--mantine-color-brand-6)" style={{ flexShrink: 0 }} />
                {/* wrap: на узком экране бейджи уходят под название культуры, а не наезжают на цену. */}
                <Group gap="xs" wrap="wrap" style={{ minWidth: 0 }}>
                  <Text fw={600} truncate>
                    {group.goods}
                  </Text>
                  <CropYearBadge year={group.year} />
                  {/* Дата «Обновлён…» — вторичное метаданное; на узком экране прячем, чтобы культура
                      и цена не накладывались (дата остаётся в детали прайс-листа). */}
                  <Box visibleFrom="sm">
                    <UpdatedDateBadge date={group.updatedDate} />
                  </Box>
                </Group>
              </Group>
              <Group gap="sm" wrap="nowrap" style={{ flexShrink: 0, marginLeft: 'auto' }}>
                <PriceRangeHint group={group} />
                <IconChevronRight size={18} color="var(--mantine-color-gray-6)" />
              </Group>
            </Group>
          </UnstyledButton>
        ))}
      </Stack>
    </Card>
  );
}
